import React from 'react';
import { Button, Input } from 'antd';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as yup from 'yup';
import { useMutation } from 'react-query';
import { forgotPasswordRequest } from 'services/apiSrv';
import { show_toast } from 'utils/helpers';
import UIOutlet from 'components/common/UIOutlet';

const FormContainer = styled.div`
    max-width: 400px;
    margin: 0 auto;
    padding: 24px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: calc(100vh - 200px); /* Adjust for header/footer */
`;

const PageHeader = styled.div`
    margin-bottom: 24px;
    text-align: center;
`;

const Title = styled.h1`
    font-size: 20px;
    font-weight: 600;
    color: #001F5C;
    margin: 0 0 6px 0;
    letter-spacing: -0.02em;
`;

const Divider = styled.div`
    width: 40px;
    height: 3px;
    background: #EF3340;
    border-radius: 2px;
    margin: 0 auto;
`;

const StyledFormItem = styled.div`
    margin-bottom: 16px;
    width: 100%;

    .form-label {
        font-size: 12px;
        font-weight: 500;
        color: #595959;
        height: 20px;
        display: block;
        margin-bottom: 4px;
    }

    .ant-input {
        border-radius: 6px;
        border-color: #e8e8e8;

        &:focus,
        &:hover {
            border-color: #00B8D4;
        }
    }

    .error-message {
        color: #ff4d4f;
        font-size: 12px;
        margin-top: 4px;
    }
`;

const ActionButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
    width: 100%;
`;

const SubmitButton = styled(Button)`
    background: #001F5C;
    border-color: #001F5C;
    height: 36px;
    padding: 0 24px;
    font-weight: 500;
    font-size: 14px;

    &:hover,
    &:focus {
        background: #00306B;
        border-color: #00306B;
    }
`;

const CancelButton = styled(Button)`
    height: 36px;
    padding: 0 24px;
    font-weight: 500;
    font-size: 14px;
    border-color: #d9d9d9;
    color: #595959;

    &:hover,
    &:focus {
        border-color: #00B8D4;
        color: #00B8D4;
    }
`;

const ForgotPassword = () => {
    const history = useHistory();

    // Validation schema for the username field
    const validationSchema = yup.object({
        username: yup.string().required('Username is required'),
    });

    // Use useMutation for handling the API call
    const { mutate, isLoading } = useMutation({
        mutationFn: forgotPasswordRequest,
        onSuccess: (response) => {
            let ref = response.data.forgotPasswordSystemRef;
            if (ref) {
                localStorage.setItem('passRef', ref);
            }
            show_toast('Password reset request sent successfully.', 'success');
            history.push('/verify-code');
        },
        onError: (error) => {
            console.error('Error sending request', error);
            show_toast(error?.message || 'Failed to send reset request. Please try again.', 'error');
        },
    });

    // Form submission handler
    const handleSubmit = (values) => {
        mutate(values);
    };

    return (
        <UIOutlet
            // title="Forgot Password"
            logoSrc="/icon.png"
            bannerTitle="Reset Your Password"
            bannerDescription="Enter your username to receive a password reset code"
        >
            <FormContainer>
                <PageHeader>
                    <Title>Forgot Password</Title>
                    <Divider />
                </PageHeader>

                <Formik
                    initialValues={{ username: '' }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {() => (
                        <Form style={{ width: '100%' }}>
                            <StyledFormItem>
                                <label className="form-label">
                                    Username(email) <span style={{ color: '#ff4d4f' }}>*</span>
                                </label>
                                <Field name="username">
                                    {({ field }) => (
                                        <Input
                                            {...field}
                                            placeholder="Enter your username"
                                        />
                                    )}
                                </Field>
                                <ErrorMessage
                                    name="username"
                                    component="div"
                                    className="error-message"
                                />
                            </StyledFormItem>

                            <ActionButtons>
                                <CancelButton onClick={() => history.push('/login')}>
                                    Back to Login
                                </CancelButton>
                                <SubmitButton
                                    type="primary"
                                    htmlType="submit"
                                    loading={isLoading}
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Processing...' : 'Send Reset Code'}
                                </SubmitButton>
                            </ActionButtons>
                        </Form>
                    )}
                </Formik>
            </FormContainer>
        </UIOutlet>
    );
};

export default ForgotPassword;